/**
 * What a pull request page's chat says above its composer while its session's sandbox starts, as
 * the session's own boot reports it: starting, whether the page preloads it for the chat's first
 * message or a message started it, or resuming a stopped one, with the step it is on. Nothing once
 * the sandbox is ready, unless its setup script still runs, as one the boot left running in the
 * background does while the agent already answers; a start that failed shows in the transcript
 * instead (`session-chat-messages.ts`).
 */
import type { BootPhaseState } from '@/app/(home)/code/_prototype/boot-phase';

import { isBootFinished } from '@/app/(home)/code/_prototype/boot-phase';
import { CODE_BOOT_PHASE } from '@/lib/code-sessions/sandbox/types';

export type SandboxStartStatus = {
  title: 'Preparing the sandbox' | 'Resuming the sandbox';
  /** The step the sandbox is on, when it says more than the title. */
  step?: string;
};

export function sandboxStartStatus({
  boot,
  row,
  resuming,
  preloading,
}: {
  /** The session's current sandbox's boot (`useCodeSession`'s `boot`). */
  boot: readonly BootPhaseState[];
  /** The session's row, null until it loads: `creating` while its startup runs. */
  row: { status: string } | null;
  /** A stopped sandbox is waking for the session. */
  resuming: boolean;
  /** The chat has not sent its first message: the session is the one the page preloads for it. */
  preloading: boolean;
}): SandboxStartStatus | null {
  if (row === null) {
    return null;
  }
  if (resuming) {
    return withStep('Resuming the sandbox', currentPhase(boot));
  }
  // The setup row runs on past a ready agent until the script reports how it ended, also while a
  // turn checks the agent again.
  const setup = boot.find((phase) => phase.phase === CODE_BOOT_PHASE.SETUP);
  const failed = boot.some((phase) => phase.status === 'failed');
  if (
    setup?.status === 'running' &&
    !failed &&
    (isBootFinished(boot) || row.status !== 'creating')
  ) {
    return withStep('Preparing the sandbox', setup);
  }
  if (isBootFinished(boot)) {
    return null;
  }
  // The row says startup runs. Before the chat's first message a phase the preload reports says so
  // too, as when the page warms the chat's swept session again after its row was read.
  const reported = boot.some((phase) => phase.status !== 'pending');
  if (row.status !== 'creating' && !(preloading && reported)) {
    return null;
  }
  return withStep(
    'Preparing the sandbox',
    currentPhase(boot) ?? boot.find((phase) => phase.status === 'pending'),
  );
}

/** The latest phase running: a phase starting leaves the earlier ones running until they report. */
function currentPhase(boot: readonly BootPhaseState[]): BootPhaseState | undefined {
  return boot.findLast((phase) => phase.status === 'running');
}

function withStep(
  title: SandboxStartStatus['title'],
  phase: BootPhaseState | undefined,
): SandboxStartStatus {
  // Creating the sandbox is what the title says already, and ready is the end of the boot.
  return phase === undefined ||
    phase.phase === CODE_BOOT_PHASE.CREATE ||
    phase.phase === CODE_BOOT_PHASE.READY
    ? { title }
    : { title, step: phase.label };
}
